import { useBoolean, useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { paramsReportToSendMapping } from '@helper/budget/commercial/paramsReportSendMappingCommercial'
import { validateGroupName } from '@helper/budget/commercial/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/budget/commercial/validateIfShowColumnInTable'
import { parseCommercialBudgetData } from '@helper/grid/budget/commercial'
import { parseCommercialBudgetDataFooter } from '@helper/grid/budget/commercial-header'
import budget from '@service/budget'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IBudgetCommercialContext,
  IParamsToSend,
  Pagination,
  IUpdateParams
} from '@base/@types/providers/Budget/Commercial'
import { reportRequest } from '@base/variables/filters/budget/comercialRequest'
import { reportFilter } from '@base/variables/filters/budget/commercialFilter'
import budgetCommercialHeader from '@base/variables/headers/budget/commercial'
const CommercialBudgetProvider: FC = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const filtersList: ILayoutFilter[] = reportFilter

  const [filename, setFilename] = useState('Orçamento Comercial - Relatório')

  const [data, updateData] = useImmer<any[]>([])
  const [dataUpdate, updateDataUpdate] = useImmer<any[]>([])
  const [query, setQuery] = useState('')
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [params, updateParams] = useImmer<any>(reportRequest)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    budgetCommercialHeader
  )
  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...reportRequest }

    return paramsFormatted
  })

  const clearAllFilters = () => {
    const paramsFormatted = { ...reportRequest }

    updateParams({
      ...params,
      empresaSelectIds: [],
      centroCdLojaIds: false,
      centroCdLojaSelectIds: [],
      categoriasIds: false,
      categoriasSelectIds: [],
      categoriaIds: true,
      categoriaSelectIds: [],
      gerenteCategoriaIds: false,
      gerenteCategoriaSelectIds: [],
      gerenteProdutoIds: false,
      gerenteProdutoSelectIds: [],
      anosIds: false,
      anosSelectIds: [],
      mesesIds: false,
      mesesSelectIds: [],
      historicoIds: false,
      historicoSelectIds: []
    })

    setParamsToSend(paramsFormatted)
    if (localStorage.getItem('budgetCommercialParams')) {
      localStorage.setItem(
        'budgetCommercialParams',
        JSON.stringify({
          ...params,
          empresaSelectIds: [],
          centroCdLojaIds: false,
          centroCdLojaSelectIds: [],
          categoriasIds: false,
          categoriasSelectIds: [],
          categoriaIds: true,
          categoriaSelectIds: [],
          gerenteCategoriaIds: false,
          gerenteCategoriaSelectIds: [],
          gerenteProdutoIds: false,
          gerenteProdutoSelectIds: [],
          anosIds: false,
          anosSelectIds: [],
          mesesIds: false,
          mesesSelectIds: [],
          historicoIds: false,
          historicoSelectIds: []
        })
      )
    }
    if (localStorage.getItem('budgetCommercialParamsRequest')) {
      localStorage.setItem(
        'budgetCommercialParamsRequest',
        JSON.stringify(paramsFormatted)
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    const paramsToSendFormatted = paramsReportToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('budgetCommercialParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        localStorage.getItem('budgetCommercialParams') || ''
      )

      localStorage.setItem(
        'budgetCommercialParams',
        JSON.stringify({ ...newParams, [name]: value })
      )
    }
    if (localStorage.getItem('budgetCommercialParamsRequest')) {
      localStorage.setItem(
        'budgetCommercialParamsRequest',
        JSON.stringify(paramsToSendFormatted)
      )
    }
  }

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('budgetCommercialParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetCommercialParams') || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem('budgetCommercialParams', JSON.stringify(params))
    }
    if (localStorage.getItem('budgetCommercialParamsRequest')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetCommercialParamsRequest') || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'budgetCommercialParamsRequest',
        JSON.stringify(paramsToSend)
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateHeaders(newHeaders)
    },
    [headers, updateHeaders]
  )
  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'budgetCommercialConsolidated',
            data,
            headers
          },
          {
            type: 'excel',
            entity: 'budgetCommercialAnalitc',
            data,
            headers
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleColumn])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        '@AlertPurchaseParamsToSend:',
        JSON.stringify(paramsToSend)
      )

      budget
        .getBudgetCommercial(paramsToSend)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    },
    [paramsToSend, updateData, updatePagination]
  )

  const updateShowTableInTable = useCallback(
    async (data: any[]) => {
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })

      const headersFormatted = validateIfShowColumnInTable(
        headers,
        paramsToSend
      )

      updateHeaders(headersFormatted)
    },
    [updateData, updateHeaders, headers, paramsToSend]
  )

  const getData = useCallback(async () => {
    setLoading(true)
    let newParams = {}
    if (localStorage.getItem('budgetCommercialParamsRequest')) {
      newParams = JSON.parse(
        localStorage.getItem('budgetCommercialParamsRequest') || ''
      )
    } else {
      newParams = paramsToSend
    }

    budget
      .getBudgetCommercial(newParams)
      .then(({ data: { data, pagination } }) => {
        updateShowTableInTable(data)
        updatePagination(pagination)
        setFilters.toggle()
        setLoading(false)
        setFilters.toggle()
      })
      .catch(() => {
        updateData([])
        setLoading(false)
        setFilters.toggle()
      })
  }, [
    paramsToSend,
    updateShowTableInTable,
    updatePagination,
    setFilters,
    updateData
  ])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        isMulti: false
      }
    ]
  }, [])
  const handleUpdateBudgetWanted = useCallback(
    async (index: number) => {
      // setLoading(true)

      let commercials = []

      commercials = data
      const commercial = commercials.filter(x => x.id === Number(index))[0]
      try {
        try {
          commercials = [...commercials] // important to create a copy, otherwise you'll modify state outside of setState call
          commercials[index] = {
            ...commercial
          }
          const newData = commercials
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(newData)
          })
          updateDataUpdate([...dataUpdate, index])
        } catch (e) {}
      } catch {}
      // setLoading(false)
    },
    [data, updateData, dataUpdate, updateDataUpdate]
  )

  const handleUpdateGrowthWanted = useCallback(
    async (index: number, wanted: number | string) => {
      let commercials = []

      commercials = data
      const commercial = commercials.filter(x => x.id === Number(index))[0]
      const idData = commercials.findIndex((x: any) => x.id === Number(index))
      try {
        try {
          commercials = [...commercials] // important to create a copy, otherwise you'll modify state outside of setState call
          commercials[idData] = {
            ...commercial,
            previsaoAnoAtual: wanted,
            alterado: true
          }
          const newData = commercials

          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(newData)
          })
          updateDataUpdate([...dataUpdate, idData])
        } catch (e) {}
      } catch {}
    },
    [data, updateData, dataUpdate, updateDataUpdate]
  )

  const exportDataConsolidated = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 9999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const obj = {
      orcamentoComercial: data,
      groupByFilterBudget: {
        year: true,
        month: true,
        company: true,
        store: true,
        compactCategory: true,
        category: true,
        categoryManager: true,
        productManager: true,
        historic: true,
        operation: true
      }
    }
    try {
      const { data: res } = await budget.postExportCommercialConsolidated(obj)
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [data, toast, filename])

  const exportDataAnalitc = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    try {
      const { data: res } = await budget.postExportCommercialAnalitc(
        paramsToSend
      )
      Swal.close()
      fileDownload(res, filename + '-analitco.csv')
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [paramsToSend, filename, toast])

  const saveUpdate = useCallback(async () => {
    setLoading(true)
    const uniqueIndexs = dataUpdate.filter((v, i, a) => a.indexOf(v) === i)
    const datasUpdates: any = []
    let auxCount = 0
    uniqueIndexs.forEach(index => {
      if (auxCount === 0) {
        datasUpdates.push({
          mes: data[index].mes ? data[index].mes : 0,
          lojaId: data[index].lojaId ? data[index].lojaId : 0,
          categoriaCompactaId: data[index].categoriaCompactaId
            ? data[index].categoriaCompactaId
            : 0,
          categoriaId: data[index].categoriaId ? data[index].categoriaId : 0,
          ano: data[index].ano ? data[index].ano : 0,
          empresaId: data[index].empresaId ? data[index].empresaId : 0,
          gerenteCategoriaId: data[index].gerenteCategoriaId
            ? data[index].gerenteCategoriaId
            : 0,
          gerenteProdutoId: data[index].gerenteProdutoId
            ? data[index].gerenteProdutoId
            : 0,
          previsaoFinal: data[index].previsaoAnoAtual
            ? data[index].previsaoAnoAtual
            : 0,
          go: 0,

          tipoOperacao: '',
          previsaoPercentual: data[index].previsaoAumentoPercentual
            ? data[index].previsaoAumentoPercentual
            : 0
        })
      } else {
        datasUpdates.push({
          mes: data[index].mes ? data[index].mes : 0,
          lojaId: data[index].lojaId ? data[index].lojaId : 0,
          categoriaCompactaId: data[index].categoriaCompactaId
            ? data[index].categoriaCompactaId
            : 0,
          categoriaId: data[index].categoriaId ? data[index].categoriaId : 0,
          ano: data[index].ano ? data[index].ano : 0,
          empresaId: data[index].empresaId ? data[index].empresaId : 0,
          gerenteCategoriaId: data[index].gerenteCategoriaId
            ? data[index].gerenteCategoriaId
            : 0,
          gerenteProdutoId: data[index].gerenteProdutoId
            ? data[index].gerenteProdutoId
            : 0,
          previsaoFinal: data[index].previsaoAnoAtual
            ? data[index].previsaoAnoAtual
            : 0,
          go: 0,
          tipoOperacao: '',
          previsaoPercentual: data[index].previsaoAumentoPercentual
            ? data[index].previsaoAumentoPercentual
            : 0
        })
      }
      auxCount++
    })
    budget
      .putCommercialBudget({ orcamentoComercial: [...datasUpdates] })
      .then(({ data: { _data } }) => {
        setLoading(false)
        toast({
          title: 'Sucesso.',
          description: 'Salvo edição em Comercial',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setLoading, dataUpdate, toast, data])

  return (
    <CommercialBudgetContext.Provider
      value={{
        loading,

        filtersList,
        pagination,
        tableInteraction,
        hiddenColumns,
        data: parseCommercialBudgetData(data),
        dataFooter: parseCommercialBudgetDataFooter(data),
        actions,
        params,
        saveUpdate,
        handlePageChange,
        handleUpdateBudgetWanted,
        handleUpdateGrowthWanted,
        headers,
        filename,
        dataUpdate,
        filters,
        query,
        setFilename,
        exportDataConsolidated,
        exportDataAnalitc,
        setQuery,
        toggleColumn,
        handleUpdateParams,
        handleLocalParams,
        getData,
        clearAllFilters,

        setFilters
      }}
    >
      {children}
    </CommercialBudgetContext.Provider>
  )
}

export default CommercialBudgetProvider
export const CommercialBudgetContext = createContext<IBudgetCommercialContext>(
  {} as IBudgetCommercialContext
)
